import isBrowser from "utils/isBrowser";

const FA_SOLID_FONT = "fas";
export const FONT_AWESOME_PREFIX = "fa-";

// Have to do some TS magic here to get it to recognize the FA
// window object
declare global {
  interface Window {
    ___FONT_AWESOME___?: {
      styles: {
        [FA_SOLID_FONT]: Record<string, string[]>;
      };
    };
  }
}

export function isFASlug(slug: string) {
  return slug.startsWith(FONT_AWESOME_PREFIX);
}

/**
 * Gets SVG Path from a font-awesome icon name (no fa- prefix)
 */
export function getFASvgInfo(iconName: string) {
  if (!isBrowser()) return null;

  const icon = window.___FONT_AWESOME___?.styles?.[FA_SOLID_FONT]?.[iconName];

  if (!icon) return null;

  // FA icon data will be an array:
  // 0 - viewport max X
  // 1 - viewport max Y
  // 2 - alt names
  // 3 - unicode character
  // 4 - svg path
  return { path: icon[4], viewBox: `0 0 ${icon[0]} ${icon[1]}` };
}

/**
 * Gets all of the icon names given a FontAwesome font name
 */
function getFAFontIconNames(fontName: "fas") {
  const font = window.___FONT_AWESOME___?.styles[fontName] || {};

  return Object.keys(font).map((key) => {
    return {
      name: key.replaceAll("-", " "),
      slug: `${FONT_AWESOME_PREFIX}${key}`,
    };
  });
}

/**
 * Gets all available Font Awesome icon names
 */
function getAllFAIconNames() {
  if (!isBrowser) return [];

  return getFAFontIconNames(FA_SOLID_FONT).sort((a, b) =>
    a.name.localeCompare(b.name)
  );
}

type FAIcon = {
  name: string;
  slug: string;
};

// Global icon list (will only be filled once)
// defer loading so we only load when the user needs it
const icons: FAIcon[] = [];

function initializeIcons() {
  if (icons.length === 0) {
    icons.push(...getAllFAIconNames());
  }
}

/**
 * Allows consumer to search for FA Icons
 */
export function useFAIcons() {
  /**
   * Returns a single FAIcon based on the name
   */
  function getIconFromName(name: string) {
    const font = window.___FONT_AWESOME___?.styles[FA_SOLID_FONT];

    const nonPrefixed = name.replace(FONT_AWESOME_PREFIX, "");

    if (font?.[nonPrefixed]) {
      return {
        name: nonPrefixed.replaceAll("-", " "),
        slug: name,
      };
    }

    return null;
  }

  /**
   * Returns an array of FA icon names that match the search string
   */
  function search(value: string) {
    initializeIcons();

    const lowerSearch = value.trim().toLowerCase();

    return icons.filter(
      (icon) =>
        icon.name.includes(lowerSearch) || icon.slug.includes(lowerSearch)
    );
  }

  return {
    getIconFromName,
    search,
  };
}
